import { Stack, Tooltip, Typography } from "@mui/material";
import Slider from "@mui/material/Slider";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase } from "../../context/CaseContext";
import { percentage } from "../../shared/percentage";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { QUIET_SLIDER } from "../general/tableStyles";
import { TonalBadge } from "../general/TonalBadge";
import { useTheme } from "../../context/ThemeContext";

/**
 * The difference of two chances is kept to this many decimals of a point:
 * 65 less 55 is 10, not the 9.999999999999993 the arithmetic leaves.
 */
const POINT_DECIMALS = 6;

/**
 * A win chance as a slider with its figure beside it. The figure follows the
 * thumb while it is dragged; the chance is reported once, when it is released.
 *
 * Given a baseline, the slider shows how far the chance is from it: the track
 * is coloured only between the two, the baseline has a mark, and a badge says
 * the difference.
 */
export const WinChanceSlider = ({
  chance = 1,
  onChanceChange,
  opponentName,
  limit = 1,
  baseline,
  changeLabel,
}: {
  chance?: number;
  onChanceChange: (newChance: number) => void;
  opponentName?: string;
  limit?: number;
  // the chance this one is a change of
  baseline?: number;
  // what the badge says of a difference to the baseline, in points; of none
  // it is called with 0
  changeLabel?: (points: number) => string;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.claims.winChance",
  });
  // every chance set with this slider is saved with the case
  const { readOnly } = useCase();
  const [sliderValue, setSliderValue] = useState(chance * 100);
  const maxSliderValue = limit * 100;
  const label = opponentName
    ? t("tooltip", { defendant: opponentName })
    : t("tooltipNoDefendant");

  const baselineValue = baseline === undefined ? undefined : baseline * 100;
  const points =
    baselineValue === undefined
      ? undefined
      : Number((sliderValue - baselineValue).toFixed(POINT_DECIMALS));
  const isChanged = points !== undefined && points !== 0;

  const marks = [
    ...(limit !== 1 ? [{ value: maxSliderValue }] : []),
    ...(baselineValue !== undefined ? [{ value: baselineValue }] : []),
  ];

  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{ alignItems: "center", flex: 1, minWidth: 0, userSelect: "none" }}
    >
      <Tooltip title={label}>
        <Slider
          color="primary"
          size="small"
          value={sliderValue}
          disabled={readOnly}
          aria-label={label}
          valueLabelFormat={(value) => percentage.format(value / 100)}
          valueLabelDisplay="off"
          onChangeCommitted={(ignored, value) => {
            if (typeof value !== "number") return;
            const normalized = Math.min(value, maxSliderValue);
            setSliderValue(normalized);
            onChanceChange(normalized / 100);
          }}
          onChange={(ignored, value) => {
            if (typeof value !== "number") return;
            const normalized = Math.min(value, maxSliderValue);
            setSliderValue(normalized);
          }}
          marks={marks}
          sx={{
            ...QUIET_SLIDER,
            flex: 1,
            minWidth: 64,
            ...(baselineValue !== undefined && {
              // the slider itself draws its track from its lowest value to
              // the thumb: here it runs between the baseline and the thumb
              "& .MuiSlider-track": {
                left: `${Math.min(baselineValue, sliderValue)}% !important`,
                width: `${Math.abs(sliderValue - baselineValue)}% !important`,
                // where the chance is the baseline there is nothing to colour
                visibility: isChanged ? "visible" : "hidden",
              },
              // the mark of the baseline stands out of the rail on both sides
              "& .MuiSlider-mark, & .MuiSlider-markActive": {
                width: 2,
                height: 14,
                borderRadius: 0.25,
                bgcolor: theme.palette.text.disabled,
                opacity: 1,
              },
            }),
          }}
        />
      </Tooltip>
      <Typography
        variant="body2"
        sx={{
          ...TABULAR_NUMERALS,
          fontWeight: 700,
          minWidth: "3.25em",
          textAlign: "right",
        }}
      >
        {percentage.format(sliderValue / 100)}
      </Typography>
      {points !== undefined && changeLabel && (
        <TonalBadge
          tone={isChanged ? theme.palette.primary : undefined}
          sx={{
            // as wide as the longest difference, so the slider keeps its
            // width while it is dragged
            minWidth: "7.5em",
            justifyContent: "center",
            ...TABULAR_NUMERALS,
          }}
        >
          {changeLabel(points)}
        </TonalBadge>
      )}
    </Stack>
  );
};
